// src/components/admin/RaiseTopUpModal.jsx
// ----------------------------------------
// Stock Top-ups Phase 5 (staff side). A draggable modal, opened from the DFV Service
// editor's Details tab, that lets Zephyr staff PREPARE a top-up FOR a service. It reuses
// the shared six-section capture form (StockTopUpStep) but, instead of submitting a live
// request, it writes a `pending` row to stock_top_up_prefill (db-163). The service later
// loads its own prefill after login and submits it READ-ONLY via the normal member path —
// so nothing hits `requests` here, and the wall stays intact.
import React from "react";
import { insertPrefill } from "../../data/stockTopUpPrefill";
import { alertDialog } from "../../services/dialogService";
import { emptyTopUpState } from "../../services/stockTopUpForm";
import { sendEmail } from "../../utils/actions";
import { portalBaseUrl } from "../../utils/appUrl";
import { logAudit } from "../../utils/auditLog";
import { ModalBackdrop } from "../form/FormUI.jsx";
import StockTopUpStep from "../form/StockTopUpStep.jsx";

export function RaiseTopUpModal({
  shelterId,
  shelterCode,
  shelterName,
  primaryEmail,
  preparedBy,
  onClose,
  onSaved,
}) {
  const [state, setState] = React.useState(emptyTopUpState());

  // Called by StockTopUpStep's "Save prepared top-up" button (after its own validation).
  // We store the raw capture STATE (TopUpState) as the payload so the service's wizard can
  // rehydrate it exactly for the read-only walkthrough — not the built lines. Returns
  // { error } on failure (StockTopUpStep shows it); on success it confirms + closes.
  async function savePrefill() {
    const note = state.note.trim() || null;
    const { data, error } = await insertPrefill(shelterId, state, note, preparedBy ?? null);
    if (error) return { error: error.message || "Could not save the prepared top-up." };
    logAudit("top_up_prefill_prepared", {
      shelter_id: shelterId,
      shelter_code: shelterCode,
      prefill_id: data?.id,
    });
    // Courtesy email to the service's primary contact (the in-app dashboard banner is the
    // reliable channel, so this is best-effort + SILENT — a failure never blocks). Link =
    // the login-gated /requests/new?prefill=1 deep-link; the prefill loads by the service's
    // own session/RLS, so the link carries no token/data.
    if (primaryEmail) {
      sendEmail("top_up_prepared", {
        to: primaryEmail,
        shelterName,
        link: `${portalBaseUrl()}/requests/new?prefill=1`,
      });
    }
    if (onSaved) onSaved();
    await alertDialog({
      title: "Top-up prepared",
      message: `Saved. ${shelterName || "The DFV Service"} will see it to review and submit after they log in${
        primaryEmail ? ", and we've emailed the primary contact a link" : ""
      }.`,
    });
    onClose();
    return {};
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      draggable
      dragTitle={`Raise Stock Top-up — ${shelterName || shelterCode || ""}`}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 980,
        maxHeight: "calc(100vh - 60px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      <div className="p-4 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
        <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
          You're preparing a Stock Top-up <strong>for this DFV Service</strong>. It is saved as a
          pending order — the service reviews and submits it themselves (read-only) after logging
          in. Nothing is ordered until they submit.
        </p>
        <StockTopUpStep
          state={state}
          setState={setState}
          onBack={onClose}
          onSubmit={savePrefill}
          submitLabel="Save prepared top-up"
          submittingLabel="Saving…"
        />
      </div>
    </ModalBackdrop>
  );
}
